import {
  Box,
  Button,
  ErrorBoundary,
  Text,
  Toast,
  useToast,
} from "@prismicio/editor-ui"
import { useMutation, useSuspenseQuery } from "@tanstack/react-query"
import { Suspense, useLayoutEffect, useState } from "react"

import { useAiABMLandingPagesFlag } from "../app/integrations/AmplitudeExperiments"
import { abmLandingPagesApiClient } from "../app/services/aiLandingPage"
import { useExistingDocument } from "./DocumentProvider"

export function LabPersonalizationFeedback() {
  return (
    <ErrorBoundary>
      <Suspense>
        <FeedbackContent />
      </Suspense>
    </ErrorBoundary>
  )
}

function FeedbackContent() {
  const { version } = useExistingDocument()
  const versionId = version.version_id

  const isAbmLandingPagesEnabled =
    useAiABMLandingPagesFlag({ active: true }) === "on"

  const { data: feedbackRunId } = useSuspenseQuery({
    queryKey: ["feedbackRunId", { versionId, isAbmLandingPagesEnabled }],
    queryFn: async () => {
      if (!isAbmLandingPagesEnabled) return null
      return await abmLandingPagesApiClient.getFeedbackRunId(versionId)
    },
  })

  if (!isAbmLandingPagesEnabled || !feedbackRunId) return null

  return <FeedbackToast feedbackRunId={feedbackRunId} versionId={versionId} />
}

interface FeedbackToastProps {
  feedbackRunId: string
  versionId: string
}

function FeedbackToast(props: FeedbackToastProps) {
  const { feedbackRunId, versionId } = props

  const { showToast } = useToast()

  const [showSurvey, setShowSurvey] = useState(true)

  useLayoutEffect(() => {
    // show the survey again if feedbackRunId changed
    setShowSurvey(true)
  }, [feedbackRunId])

  const { mutate: submitFeedback } = useMutation({
    onMutate: () => {
      // optimistic success message
      setShowSurvey(false)
      showToast({
        icon: "check",
        title: "Thanks for your feedback!",
      })
    },
    mutationFn: async (score: number) =>
      await abmLandingPagesApiClient.createFeedback({
        runId: feedbackRunId,
        score: score * 10,
        versionId,
      }),
  })

  return (
    <Toast
      variant="card"
      anchor={
        <Box
          position="fixed"
          width="100%"
          justifyContent="center"
          bottom={72}
        />
      }
      title="We need your feedback!"
      open={showSurvey}
      onOpenChange={setShowSurvey}
      subtitle={
        <Box as="span" gap={8} flexDirection="column" alignItems="center">
          <Text color="grey11">
            How satisfied are you with this personalized landing page?
          </Text>
          <ScaleFeedBack submitFeedback={submitFeedback} />
        </Box>
      }
    />
  )
}

interface ScaleFeedBackProps {
  submitFeedback: (score: number) => void
}

function ScaleFeedBack(props: ScaleFeedBackProps) {
  const { submitFeedback } = props
  return (
    <Box as="span" gap={4} justifyContent="center" flexDirection="row">
      <Button color="grey" size="small" onClick={() => submitFeedback(1)}>
        1
      </Button>
      <Button color="grey" size="small" onClick={() => submitFeedback(2)}>
        2
      </Button>
      <Button color="grey" size="small" onClick={() => submitFeedback(3)}>
        3
      </Button>
      <Button color="grey" size="small" onClick={() => submitFeedback(4)}>
        4
      </Button>
      <Button color="grey" size="small" onClick={() => submitFeedback(5)}>
        5
      </Button>
      <Button color="grey" size="small" onClick={() => submitFeedback(6)}>
        6
      </Button>
      <Button color="grey" size="small" onClick={() => submitFeedback(7)}>
        7
      </Button>
      <Button color="grey" size="small" onClick={() => submitFeedback(8)}>
        8
      </Button>
      <Button color="grey" size="small" onClick={() => submitFeedback(9)}>
        9
      </Button>
      <Button color="grey" size="small" onClick={() => submitFeedback(10)}>
        10
      </Button>
    </Box>
  )
}
